import { useState } from "react";

import type { DocumentFile } from "./schema";

/**
 * Shared building blocks for the document pickers that feed the legacy signing wizard
 * (Administration → Signature Requests, and the workflow to-do form). The two render
 * differently — one is select-then-confirm, the other commits on click — so only the
 * non-visual logic lives here.
 */

/** The signing wizard only accepts PDFs, so a non-PDF in a picker would be a dead end. */
export const isPdf = (doc: DocumentFile): boolean =>
	doc.content_type === "application/pdf" || doc.filename.toLowerCase().endsWith(".pdf");

export type PickableDocument = {
	id: string;
	name: string;
};

/** Filters to PDFs and reduces each to just what a picker row renders. */
export const toPickableDocuments = (docs: DocumentFile[]): PickableDocument[] =>
	docs.filter(isPdf).map((doc) => ({ id: doc._id, name: doc.filename }));

/**
 * The current user's real first/last name.
 *
 * `user.me.aliases` holds company aliases, not a name, so the only source is the
 * server-rendered `window._MyUser` global. Returns "" when it isn't present, which
 * callers fall back on with a generic "My Documents" label.
 */
export const getCurrentUserName = (): string => window._MyUser?.name ?? "";

export type FolderAccordion = {
	isExpanded: (key: string) => boolean;
	toggle: (key: string) => void;
	collapseAll: () => void;
	isLocked: boolean;
};

/**
 * Expand/collapse state for a picker's folder list.
 *
 * `forceExpanded` is for the searching case: a search is only useful if you can see
 * what it matched, so matching folders open on their own. While it is set, `toggle` is
 * deliberately inert — accepting clicks would flip hidden state that changes nothing on
 * screen and then pops folders open once the search clears.
 */
export const useFolderAccordion = (forceExpanded = false): FolderAccordion => {
	const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});

	return {
		isExpanded: (key) => forceExpanded || !!expandedFolders[key],
		toggle: (key) => {
			if (forceExpanded) {
				return;
			}
			setExpandedFolders((previous) => ({ ...previous, [key]: !previous[key] }));
		},
		collapseAll: () => setExpandedFolders({}),
		isLocked: forceExpanded,
	};
};
